import React, { useMemo, useState } from 'react';

import { UiComponent, UserGroupComponent } from 'components';
import { StickerQuery } from 'queries';
import useModal from 'hooks/useModal';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { ISticker, IUserGroupCandidate } from 'types';
import * as GrantStyle from './style';

interface Props {
  sticker: ISticker;
}

function Grant({ sticker }: Props) {
  const { handleConfirm } = useModal();
  const { mutateAsync: grant } = StickerQuery.useGrant();
  const { mutateAsync: revoke } = StickerQuery.useRevoke();
  const [selected, setSelected] = useState<IUserGroupCandidate[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedIds = useMemo(() => selected.map((u) => u.id), [selected]);

  const handleGrant = () => {
    if (isSubmitting || selectedIds.length === 0) return;

    handleConfirm({
      confirmContent: `총 ${selectedIds.length.toLocaleString('ko')}명에게 '${sticker.name}' 스티커를 지급하시겠습니까?`,
      yesTitle: '지급',
      confirmNext: async () => {
        setIsSubmitting(true);
        try {
          const res = await grant({ id: sticker.id, userIds: selectedIds });
          const { granted, skipped } = res.data;
          toast.success(
            `${granted}명 지급 완료${skipped ? ` (이미 보유 ${skipped}명 건너뜀)` : ''}`,
          );
          setSelected([]);
        } catch {
          toast.error(DEFAULT_ERROR_MESSAGE);
        } finally {
          setIsSubmitting(false);
        }
      },
    });
  };

  const handleRevoke = () => {
    if (isSubmitting || selectedIds.length === 0) return;

    handleConfirm({
      confirmContent: `총 ${selectedIds.length.toLocaleString('ko')}명에게서 '${sticker.name}' 스티커를 회수하시겠습니까?`,
      yesTitle: '회수',
      confirmNext: async () => {
        setIsSubmitting(true);
        try {
          const res = await revoke({ id: sticker.id, userIds: selectedIds });
          const { revoked, skipped } = res.data;
          toast.success(
            `${revoked}명 회수 완료${skipped ? ` (미보유 ${skipped}명 건너뜀)` : ''}`,
          );
          setSelected([]);
        } catch {
          toast.error(DEFAULT_ERROR_MESSAGE);
        } finally {
          setIsSubmitting(false);
        }
      },
    });
  };

  return (
    <div css={GrantStyle.wrap}>
      <div css={GrantStyle.stickerSummary}>
        <div>
          {sticker.imageUrl && (
            <img src={sticker.imageUrl} alt={sticker.name} />
          )}
        </div>
        <p>
          <strong>{sticker.name}</strong>
          <span css={{ marginLeft: 6, color: '#8B95A1' }}>{sticker.code}</span>
        </p>
      </div>

      <UserGroupComponent.Picker
        selected={selected}
        onChange={setSelected}
        sources={['USER', 'GROUP']}
      />

      <div css={GrantStyle.footer}>
        <UiComponent.Button
          variant="border"
          color="error"
          disabled={isSubmitting || selectedIds.length === 0}
          onClick={handleRevoke}
        >
          회수
        </UiComponent.Button>
        <UiComponent.Button
          variant="contained"
          color="primary"
          disabled={isSubmitting || selectedIds.length === 0}
          onClick={handleGrant}
        >
          지급
        </UiComponent.Button>
      </div>
    </div>
  );
}

export default Grant;
